import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '@ups/xplat/core';
import { BRIDGE_REGISTER_PATH } from '../models/bridge.models';

/**
 * Pluggable JWT supplier so the interceptor stays free of platform-specific
 * code. The Roost web app implements this with the host-registration flow
 * (MSAL identity + localStorage-cached PSK -> bridge JWT). NativeScript or
 * other apps can provide their own implementation without touching the
 * interceptor.
 */
export interface BridgeJwtProvider {
  /** Resolves to a fresh JWT or null when none can be obtained. */
  getJwt(): Promise<string | null>;
  /**
   * Force re-acquisition of the JWT. The interceptor calls this once after
   * a 401 so an expired token gets refreshed without bouncing the user.
   */
  refreshJwt(): Promise<string | null>;
}

export const BRIDGE_JWT_PROVIDER = new InjectionToken<BridgeJwtProvider>(
  'BRIDGE_JWT_PROVIDER'
);

/**
 * Attaches `Authorization: Bearer <jwt>` to every request that targets the
 * Ryn Bridge. The JWT itself comes from a `BridgeJwtProvider` registered
 * by the host app — see the Roost web app for the host-registration flow
 * that backs this on the browser side.
 *
 * On 401 the interceptor asks the provider to refresh once and retries the
 * original request. If no provider is registered, the request is sent
 * without an Authorization header (and will 401 against any protected
 * bridge endpoint, which is the correct surface error).
 *
 * If a provider IS registered but `getJwt()` resolves to null — which
 * happens when the auth flow has parked in a terminal state like
 * `machine-locked` or `denied` — the interceptor short-circuits with
 * `EMPTY` instead of firing the doomed request. Two reasons:
 *
 *   1. The bridge would return 401, and the workspace-wide
 *      `GlobalErrorHandler` reacts to ANY 401 by calling
 *      `window.location.reload()` (legacy Auth0 behaviour). That would
 *      relaunch the page, re-run bootstrap, hit `/register` → 409 →
 *      machine-locked again, and immediately re-fire the same dashboard
 *      requests — producing the full-page refresh loop Tim hit during
 *      Roost storage-reset testing.
 *   2. The bridge-auth modal is already up telling the user how to
 *      recover (Try again / Reset storage); silently completing
 *      observables is the least-noisy way to keep components in their
 *      initial empty state while the user resolves the lockout.
 */
@Injectable({ providedIn: 'root' })
export class BridgeAuthInterceptor implements HttpInterceptor {
  private readonly provider = inject(BRIDGE_JWT_PROVIDER, { optional: true });

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const bridgeBase = environment.urls?.bridgeAPI;
    if (!bridgeBase || !req.url.startsWith(bridgeBase)) {
      return next.handle(req);
    }

    // Bootstrap endpoints have no JWT to attach — they're how we get one.
    // /register (PSK -> JWT exchange) and /approval/status (poll while
    // waiting for Tim's adaptive-card approval) must stay anonymous.
    if (
      req.url.endsWith(BRIDGE_REGISTER_PATH) ||
      req.url.includes('/approval/status')
    ) {
      return next.handle(req);
    }

    if (!this.provider) {
      return next.handle(req);
    }

    return from(this.provider.getJwt()).pipe(
      switchMap((token) => {
        if (!token) {
          // Provider couldn't (or wouldn't) hand out a JWT — typically
          // because the bridge auth flow is parked in machine-locked /
          // denied / no-account / error. Sending the request anyway is
          // pointless (guaranteed 401) and would trip the workspace
          // GlobalErrorHandler's reload-on-401 fallback, refreshing the
          // page over and over while the modal is up. Complete silently
          // and let the modal drive recovery.
          return EMPTY;
        }
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        });
        return next.handle(authReq).pipe(
          catchError((err: unknown) => {
            if (
              err instanceof HttpErrorResponse &&
              err.status === 401 &&
              this.provider
            ) {
              return from(this.provider.refreshJwt()).pipe(
                switchMap((fresh) => {
                  if (!fresh) return throwError(() => err);
                  const retry = req.clone({
                    setHeaders: { Authorization: `Bearer ${fresh}` },
                  });
                  return next.handle(retry);
                })
              );
            }
            return throwError(() => err);
          })
        );
      })
    );
  }
}
